/**
 * SOESO-LA01 · P2-E Rev 1.3 · line-wise Add / Cancel (unpacked) / Close.
 * Pure frontend over the accepted backend: qty-position + /orders/{so|eso}/{id}/lines/{ref}/{add-qty|cancel|close}.
 * All quantities are backend-authoritative; nothing is computed or optimistically changed here.
 */
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LEGACY_SPLIT_LABEL, PROTECTION_TEXT, legacySplitUnavailable } from "@/lib/qtyDisplay";

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return `${d.code ? d.code + " · " : ""}${d.message || ""}`;
  return d || e?.message || "Request failed";
};
const ACTIONS = { add: "Add Qty", cancel: "Cancel Qty", close: "Close Qty" };

function PositionGrid({ pos, cancelledOrder, testid }) {
  // R2-01L A4-02 · D1 never splits a legacy total · G1 cancelled order shows no pending.
  const cxCells = legacySplitUnavailable(pos)
    ? [[LEGACY_SPLIT_LABEL, pos.cancelled_qty, "legacy-cancelled"], ["Total Cancelled", pos.cancelled_qty, "total-cancelled"]]
    : [["Cancelled", pos.C, "cancelled"], ["Closed", pos.K, "closed"]];
  const cells = [
    ["Original Ordered", pos.O, "orig"], ["Additional", pos.A, "added"], ...cxCells,
    ["Effective", pos.E, "effective"], ["Packed (undelivered)", pos.P, "packed"],
    ["Delivered", pos.D, "delivered"], ["Pending", cancelledOrder ? 0 : pos.pending, "pending"],
  ];
  return (
    <div className="grid grid-cols-4 gap-2" data-testid={testid}>
      {cells.map(([l, v, k]) => (
        <div key={k} className="border border-[var(--border)] rounded-sm px-2 py-1.5">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">{l}{k === "orig" ? " · read-only" : ""}</div>
          <div className="font-mono text-sm tabular-nums" data-testid={`${testid}-${k}`}>{v === null || v === undefined ? "Not established" : fmt(v)}</div>
        </div>
      ))}
    </div>
  );
}

function History({ data }) {
  const [open, setOpen] = useState(false);
  const ledger = data.qty_ledger || [];
  const hist = data.line_history || [];
  return (
    <div className="border border-[var(--border)] rounded-sm" data-testid="lqa-history">
      <button data-testid="lqa-history-toggle" onClick={() => setOpen((o) => !o)}
        className="w-full text-left px-2 py-1.5 text-[10px] uppercase tracking-widest text-[var(--accent)] hover:bg-[var(--bg-2)] transition-colors">
        {open ? "▾" : "▸"} Amendment History · {ledger.length} ledger · {hist.length} history
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto text-[11px]">
          <table className="w-full">
            <thead><tr className="text-zinc-500 text-[9px] uppercase"><th className="text-left px-2 py-1">When</th><th className="text-left px-2">Op</th><th className="text-right px-2">Qty</th><th className="text-left px-2">By</th><th className="text-left px-2">Reason</th></tr></thead>
            <tbody>
              {ledger.map((x) => (
                <tr key={x.entry_id} className="border-t border-[var(--border)]" data-testid={`lqa-ledger-${x.entry_id}`}>
                  <td className="px-2 py-1 font-mono">{(x.at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 font-mono">{x.op}{x.reversed_by ? " (reversed)" : ""}</td>
                  <td className="px-2 text-right font-mono">{fmt(x.qty)}</td>
                  <td className="px-2" data-testid={`lqa-ledger-by-${x.entry_id}`}>{x.by_email}</td><td className="px-2 text-zinc-400">{x.reason}</td>
                </tr>
              ))}
              {hist.map((h, i) => (
                <tr key={`h-${i}`} className="border-t border-[var(--border)] text-zinc-400" data-testid={`lqa-line-history-${i}`}>
                  <td className="px-2 py-1 font-mono">{(h.at || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-2 font-mono">{h.action}</td>
                  <td className="px-2 text-right font-mono">{fmt(h.qty ?? h.closed_qty ?? h.packed_qty)}</td>
                  <td className="px-2">{h.by}</td><td className="px-2">{h.reason}</td>
                </tr>
              ))}
              {!ledger.length && !hist.length && <tr><td colSpan={5} className="px-2 py-2 text-zinc-500">No amendments recorded.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActionForm({ kind, data, onPosted }) {
  const pos = data.position;
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [po, setPo] = useState("");
  const [instr, setInstr] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [key, setKey] = useState(uuidv4());
  const amount = kind === "close" ? pos.eligible_close : Number(qty || 0);
  const valid = reason.trim().length >= 4 && amount > 0 && (kind === "close" || po.trim()) && (kind !== "cancel" || instr.trim())
    && (kind !== "cancel" || amount <= pos.U);
  const tid = `lqa-${kind}`;

  const submit = async () => {
    setBusy(true); setErr("");
    const body = { reason: reason.trim(), expected: { article_no: data.article_no || "", order_qty: pos.E, guard_rev: data.guard_rev } };
    if (kind === "add") Object.assign(body, { qty: amount, customer_po_ref: po.trim() });
    if (kind === "cancel") Object.assign(body, { unpacked_qty: amount, customer_po_ref: po.trim(), customer_instruction_ref: instr.trim() });
    const action = kind === "add" ? "add-qty" : kind;
    try {
      await api.post(`/orders/${data._ot}/${data.order_id}/lines/${encodeURIComponent(data.line_ref)}/${action}`, body,
        { headers: { "Idempotency-Key": key } });
      toast.success(`${ACTIONS[kind]} posted · ${fmt(amount)} m`);
      setKey(uuidv4()); setConfirm(false); setQty(""); setReason(""); setPo(""); setInstr("");
      await onPosted();
    } catch (e) {
      setErr(errText(e)); setConfirm(false); setKey(uuidv4());
    } finally { setBusy(false); }
  };

  const inp = "w-full h-8 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm px-2 text-xs font-mono focus:outline-none focus:border-[var(--accent)]";
  return (
    <div className="space-y-2" data-testid={`${tid}-form`}>
      {kind === "close" ? (
        <div className="text-xs">Eligible unpacked balance (pending − packed), closed in full:{" "}
          <span className="font-mono font-semibold" data-testid="lqa-close-eligible">{fmt(pos.eligible_close)} m</span></div>
      ) : (
        <label className="block text-[10px] uppercase tracking-widest text-zinc-500">
          {kind === "add" ? "Additional quantity (m)" : `Unpacked quantity to cancel (max ${fmt(pos.U)} m)`}
          <input data-testid={`${tid}-qty`} type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className={inp} />
        </label>
      )}
      {kind !== "close" && (
        <label className="block text-[10px] uppercase tracking-widest text-zinc-500">Customer PO reference
          <input data-testid={`${tid}-po`} value={po} onChange={(e) => setPo(e.target.value)} className={inp} /></label>
      )}
      {kind === "cancel" && (
        <label className="block text-[10px] uppercase tracking-widest text-zinc-500">Customer instruction reference
          <input data-testid={`${tid}-instruction`} value={instr} onChange={(e) => setInstr(e.target.value)} className={inp} /></label>
      )}
      <label className="block text-[10px] uppercase tracking-widest text-zinc-500">Reason (min 4 characters)
        <input data-testid={`${tid}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} className={inp} /></label>
      {err && <div data-testid={`${tid}-error`} className="text-xs text-red-300 border border-red-500/40 bg-red-500/10 rounded-sm px-2 py-1">{err}</div>}
      {!confirm ? (
        <button data-testid={`${tid}-review`} disabled={!valid || busy} onClick={() => setConfirm(true)}
          className="h-8 px-3 border border-[var(--accent)] text-[var(--accent)] rounded-sm text-[10px] uppercase tracking-widest disabled:opacity-40 hover:bg-[var(--accent)]/10 transition-colors">Review</button>
      ) : (
        <div className="flex items-center gap-2 text-xs" data-testid={`${tid}-confirm-box`}>
          <span>Confirm {ACTIONS[kind]} of <b className="font-mono">{fmt(amount)} m</b> on {data.order_no} · {data.article_no}?</span>
          <button data-testid={`${tid}-confirm`} disabled={busy} onClick={submit}
            className="h-7 px-3 bg-[var(--accent)] text-black rounded-sm text-[10px] uppercase tracking-widest disabled:opacity-50">{busy ? "Posting…" : "Confirm"}</button>
          <button data-testid={`${tid}-back`} disabled={busy} onClick={() => setConfirm(false)} className="h-7 px-2 border border-[var(--border)] rounded-sm text-[10px] uppercase">Back</button>
        </div>
      )}
    </div>
  );
}

export function LineQtyActions({ otype, orderId, lineRef, articleNo, onChanged, testid, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [kind, setKind] = useState("add");

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      let d;
      if (readOnly) {
        // R2-01L A4-02 · D2 · viewers read the batch breakdown (no ledger, no guard revision).
        const { data: all } = await api.get(`/orders/${otype}/${orderId}/qty-positions`);
        const ln = (all.lines || []).find((x) => x.line_ref === lineRef);
        if (!ln) { setLoadErr("LINE_NOT_FOUND · reopen the order to refresh"); setData(null); return; }
        d = { ...ln, order_no: all.order_no, lifecycle: all.lifecycle, order_id: orderId };
      } else {
        d = (await api.get(`/orders/${otype}/${orderId}/lines/${encodeURIComponent(lineRef)}/qty-position`)).data;
      }
      if ((d.article_no || "") !== (articleNo || "")) { setLoadErr("LINE_IDENTITY_MISMATCH · reopen the order to refresh"); setData(null); return; }
      setData({ ...d, _ot: otype });
    } catch (e) { setLoadErr(errText(e)); setData(null); }
  }, [otype, orderId, lineRef, articleNo, readOnly]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const posted = async () => { await load(); onChanged && (await onChanged()); };
  const pos = data?.position;
  const cancelledOrder = data?.lifecycle === "Cancelled";
  const noOrig = pos && (pos.O === null || pos.O === undefined);
  // R2-01L A4-02 · G3 · protection class first; a missing original is a secondary note.
  const blocked = !pos ? "" : cancelledOrder ? "ORDER_CANCELLED · this order is cancelled — no pending quantity"
    : data.readiness_block ? `LINE_READINESS_BLOCKED · ${data.readiness_block} · ${PROTECTION_TEXT[data.readiness_block] || ""}${noOrig ? " · original ordered quantity not established" : ""}`
    : noOrig ? "ORIGINAL_QTY_NOT_ESTABLISHED · original ordered quantity has not been established for this line" : "";

  return (
    <>
      <button data-testid={testid} onClick={(e) => { e.stopPropagation(); setOpen(true); }} title={readOnly ? "Line quantity breakdown (read-only)" : "Line quantity: Add / Cancel / Close"}
        className="h-5 px-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--accent)] rounded-sm text-[9px] font-mono uppercase tracking-widest transition-colors">Qty</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="lqa-dialog">
          <DialogHeader><DialogTitle className="text-sm">Line Quantity · {data?.order_no || ""} · <span className="font-mono">{articleNo}</span> · {lineRef}</DialogTitle></DialogHeader>
          {loadErr && <div data-testid="lqa-load-error" className="text-xs text-red-300">{loadErr}</div>}
          {data && (
            <div className="space-y-3">
              <PositionGrid pos={pos} cancelledOrder={cancelledOrder} testid="lqa-position" />
              {readOnly ? (
                <div data-testid="lqa-read-only" className="text-xs text-zinc-400 border border-[var(--border)] rounded-sm px-2 py-1.5">
                  {blocked ? `${blocked} · ` : ""}Read-only view · quantity changes are Administrator-only.</div>
              ) : blocked ? (
                <div data-testid="lqa-blocked" className="text-xs text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-sm px-2 py-1.5">{blocked} · Add, Cancel and Close are disabled.</div>
              ) : (
                <>
                  <div className="flex gap-1" role="tablist">
                    {Object.entries(ACTIONS).map(([k, l]) => (
                      <button key={k} data-testid={`lqa-tab-${k}`} onClick={() => setKind(k)}
                        className={`h-7 px-3 rounded-sm text-[10px] uppercase tracking-widest border transition-colors ${kind === k ? "border-[var(--accent)] text-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] text-zinc-400"}`}>{l}</button>
                    ))}
                  </div>
                  <ActionForm key={`${kind}-${data.guard_rev}`} kind={kind} data={data} onPosted={posted} />
                </>
              )}
              {!readOnly && <History data={data} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LineQtyActions;
